import { useEffect, useState, useCallback } from 'react';
import { AfiliacionesService } from '../../../services/afiliacionesServices';
import theme from '../adminTheme';
import AdminSpinner from '../components/AdminSpinner';
import ConfirmModal from '../components/ConfirmModal';
import AdminPagination from '../components/AdminPagination';
import AdminModal from '../components/AdminModal';
import { useToast } from '../context/ToastContext';

const badgeAfiliacion = (estado) => ({
  padding: '4px 12px',
  borderRadius: 14,
  fontSize: 12,
  fontWeight: 600,
  color: estado === 'activa' ? '#2e7d32' : estado === 'vencida' ? '#ef6c00' : estado === 'pendiente' ? '#1976d2' : '#c62828',
  background: estado === 'activa' ? '#e8f5e9' : estado === 'vencida' ? '#fff3e0' : estado === 'pendiente' ? '#e3f2fd' : '#ffebee'
});

const AfiliacionesView = () => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    usuario_id: '',
    tipo_afiliacion: '',
    fecha_inicio: '',
    fecha_fin: '',
    estado: '',
    monto: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { showToast } = useToast();

  // Paginación y búsqueda
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchAfiliaciones = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await AfiliacionesService.listarAdmin(page, 10, search);
      setItems(data.items || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        total_pages: 1,
        has_next: false,
        has_prev: false
      });
    } catch (err) {
      console.error('Error al cargar afiliaciones:', err);
      setError(err.message || 'Error al cargar las afiliaciones');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchAfiliaciones();
  }, [fetchAfiliaciones]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      setPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (pagination.has_prev) {
      setPage(prev => prev - 1);
    }
  };

  const handleOpenModal = (item = null) => {
    setFormErrors({});
    if (item) {
      setEditingItem(item);
      setFormData({
        usuario_id: item.usuario?.rut || '',
        tipo_afiliacion: item.tipo_afiliacion || '',
        fecha_inicio: item.fecha_inicio || '',
        fecha_fin: item.fecha_fin || '',
        estado: item.estado || '',
        monto: item.monto ?? ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        usuario_id: '',
        tipo_afiliacion: '',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_fin: '',
        estado: '',
        monto: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormErrors({});
    setFormData({
      usuario_id: '',
      tipo_afiliacion: '',
      fecha_inicio: '',
      fecha_fin: '',
      estado: '',
      monto: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const err = {};
    if (!formData.tipo_afiliacion || formData.tipo_afiliacion === '') {
      err.tipo_afiliacion = 'Debe elegir un tipo de afiliación';
    }
    if (!formData.estado || formData.estado === '') {
      err.estado = 'Debe elegir un estado';
    }
    if (formData.fecha_inicio && formData.fecha_fin) {
      if (new Date(formData.fecha_inicio) > new Date(formData.fecha_fin)) {
        err.fecha_fin = 'La fecha de fin debe ser posterior a la fecha de inicio';
      }
    }
    if (formData.monto !== '' && formData.monto != null) {
      const montoNum = parseFloat(formData.monto);
      if (isNaN(montoNum) || montoNum < 0) {
        err.monto = 'El monto debe ser un número mayor o igual a 0';
      }
    }
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setError(null);

    try {
      const dataToSend = { ...formData };
      if (dataToSend.monto !== '' && dataToSend.monto != null) {
        dataToSend.monto = parseFloat(dataToSend.monto);
      }

      if (editingItem) {
        await AfiliacionesService.editar(editingItem.id, dataToSend);
        setSuccess('Afiliación actualizada exitosamente');
      } else {
        await AfiliacionesService.crear(dataToSend);
        setSuccess('Afiliación creada exitosamente');
      }

      handleCloseModal();
      fetchAfiliaciones();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Error al guardar afiliación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      await AfiliacionesService.eliminar(confirmDeleteId);
      setConfirmDeleteId(null);
      showToast('Afiliación eliminada correctamente', 'success');
      fetchAfiliaciones();
    } catch (err) {
      setError(err.message || 'Error al eliminar afiliación');
    } finally {
      setDeleting(false);
    }
  };

  if (loading && items.length === 0) {
    return (
      <div style={theme.container}>
        <h1 style={theme.title}>Afiliaciones</h1>
        <p style={theme.subtitle}>Gestión de afiliaciones de usuarios</p>
        <AdminSpinner label="Cargando afiliaciones..." />
      </div>
    );
  }

  return (
    <div style={theme.container}>
      <div style={theme.pageHeader}>
        <div>
          <h1 style={theme.title}>Afiliaciones</h1>
          <p style={theme.subtitle}>Gestión de afiliaciones de usuarios</p>
        </div>
        <form onSubmit={handleSearch} style={theme.searchContainer}>
          <input
            type="text"
            placeholder="Buscar por RUT, nombre o apellido..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={theme.searchInput}
            aria-label="Buscar afiliaciones"
          />
          <button type="submit" style={theme.btnSecondary}>Buscar</button>
          {search && (
            <button type="button" style={theme.btnSecondary} onClick={handleClearSearch}>
              Limpiar
            </button>
          )}
        </form>
        <button style={theme.btnPrimary} onClick={() => handleOpenModal()} aria-label="Nueva afiliación">
          + Nueva Afiliación
        </button>
      </div>

      {error && <div style={theme.error}><strong>Error:</strong> {error}</div>}
      {success && <div style={theme.success}>{success}</div>}

      <div style={theme.card}>
        {items.length === 0 ? (
          <p style={theme.empty}>
            {search ? `No se encontraron afiliaciones para "${search}"` : 'No hay afiliaciones registradas'}
          </p>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={theme.table}>
                <thead>
                  <tr>
                    <th style={theme.th}>RUT</th>
                    <th style={theme.th}>Nombre</th>
                    <th style={theme.th}>Tipo</th>
                    <th style={theme.th}>Número</th>
                    <th style={theme.th}>Inicio</th>
                    <th style={theme.th}>Vencimiento</th>
                    <th style={theme.th}>Estado</th>
                    <th style={theme.th}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(a => (
                    <tr key={a.id}>
                      <td style={theme.td}>{a.usuario?.rut || '—'}</td>
                      <td style={theme.td}>{a.usuario?.nombre || ''} {a.usuario?.apellido || ''}</td>
                      <td style={theme.td}>{a.tipo_afiliacion || '—'}</td>
                      <td style={theme.td}>{a.numero_afiliacion || '—'}</td>
                      <td style={theme.td}>{a.fecha_inicio ? new Date(a.fecha_inicio).toLocaleDateString() : '—'}</td>
                      <td style={theme.td}>{a.fecha_fin ? new Date(a.fecha_fin).toLocaleDateString() : '—'}</td>
                      <td style={theme.td}>
                        <span style={badgeAfiliacion(a.estado || 'desconocido')}>{a.estado || 'desconocido'}</span>
                      </td>
                      <td style={theme.td}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button style={theme.btnSecondary} onClick={() => handleOpenModal(a)} aria-label="Editar afiliación">Editar</button>
                          <button style={theme.btnDanger} onClick={() => setConfirmDeleteId(a.id)} aria-label="Eliminar afiliación">Eliminar</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <AdminPagination
              page={pagination.page}
              totalPages={pagination.total_pages}
              total={pagination.total}
              pageSize={10}
              hasPrev={pagination.has_prev}
              hasNext={pagination.has_next}
              onPrev={handlePrevPage}
              onNext={handleNextPage}
              label="afiliaciones"
            />
          </>
        )}
      </div>

      <ConfirmModal
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Eliminar afiliación"
        message="¿Estás seguro de eliminar esta afiliación?"
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deleting}
      />

      {/* Modal formulario */}
      {showModal && (
        <AdminModal open={true} onClose={handleCloseModal} title={editingItem ? 'Editar Afiliación' : 'Nueva Afiliación'} width={500}>
          <form onSubmit={handleSubmit}>
              {!editingItem && (
                <div style={theme.formGroup}>
                  <label style={theme.label}>RUT del Usuario *</label>
                  <input
                    type="text"
                    name="usuario_id"
                    value={formData.usuario_id}
                    onChange={handleInputChange}
                    placeholder="Ej: 12345678-9"
                    style={theme.input}
                    required
                  />
                </div>
              )}

              <div style={theme.formGroup}>
                <label style={theme.label}>Tipo de Afiliación *</label>
                <select
                  name="tipo_afiliacion"
                  value={formData.tipo_afiliacion}
                  onChange={handleInputChange}
                  style={{ ...theme.select, ...(formErrors.tipo_afiliacion ? { borderColor: '#c62828' } : {}) }}
                  required
                  aria-invalid={!!formErrors.tipo_afiliacion}
                  aria-describedby={formErrors.tipo_afiliacion ? 'error-tipo_afiliacion' : undefined}
                >
                  <option value="">Tipo de afiliación</option>
                  <option value="anual">Anual</option>
                  <option value="semestral">Semestral</option>
                  <option value="mensual">Mensual</option>
                </select>
                {formErrors.tipo_afiliacion && (
                  <span id="error-tipo_afiliacion" style={{ color: '#c62828', fontSize: 12, marginTop: 4, display: 'block' }}>{formErrors.tipo_afiliacion}</span>
                )}
              </div>

              <div style={theme.formGroup}>
                <label style={theme.label}>Fecha de Inicio</label>
                <input
                  type="date"
                  name="fecha_inicio"
                  value={formData.fecha_inicio}
                  onChange={handleInputChange}
                  style={theme.input}
                />
              </div>

              <div style={theme.formGroup}>
                <label style={theme.label}>Fecha de Fin</label>
                <input
                  type="date"
                  name="fecha_fin"
                  value={formData.fecha_fin}
                  onChange={handleInputChange}
                  style={{ ...theme.input, ...(formErrors.fecha_fin ? { borderColor: '#c62828' } : {}) }}
                  aria-invalid={!!formErrors.fecha_fin}
                />
                {formErrors.fecha_fin && (
                  <span style={{ color: '#c62828', fontSize: 12, marginTop: 4, display: 'block' }}>{formErrors.fecha_fin}</span>
                )}
              </div>

              <div style={theme.formGroup}>
                <label style={theme.label}>Estado *</label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                  style={{ ...theme.select, ...(formErrors.estado ? { borderColor: '#c62828' } : {}) }}
                  required
                  aria-invalid={!!formErrors.estado}
                  aria-describedby={formErrors.estado ? 'error-estado' : undefined}
                >
                  <option value="">Elegir Estado</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="activa">Activa</option>
                  <option value="vencida">Vencida</option>
                  <option value="cancelada">Cancelada</option>
                </select>
                {formErrors.estado && (
                  <span id="error-estado" style={{ color: '#c62828', fontSize: 12, marginTop: 4, display: 'block' }}>{formErrors.estado}</span>
                )}
              </div>

              <div style={theme.formGroup}>
                <label style={theme.label}>Monto (CLP)</label>
                <input
                  type="number"
                  name="monto"
                  value={formData.monto}
                  onChange={handleInputChange}
                  placeholder="Ej: 50000"
                  min={0}
                  step="1"
                  style={{ ...theme.input, ...(formErrors.monto ? { borderColor: '#c62828' } : {}) }}
                  aria-invalid={!!formErrors.monto}
                />
                {formErrors.monto && (
                  <span style={{ color: '#c62828', fontSize: 12, marginTop: 4, display: 'block' }}>{formErrors.monto}</span>
                )}
              </div>

              <div style={theme.modalActions}>
                <button
                  type="button"
                  style={theme.btnSecondary}
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={theme.btnPrimary}
                  disabled={submitting}
                >
                  {submitting ? 'Guardando...' : (editingItem ? 'Actualizar' : 'Crear')}
                </button>
              </div>
          </form>
        </AdminModal>
      )}
    </div>
  );
};

export default AfiliacionesView;
